import type { RegionalMultiplierEntry } from "@reno-estimator/core";

/**
 * Initial sample regional multiplier dataset. Every entry is an
 * "Administrator supplied sample value" - a plausible relative-cost
 * placeholder, not a number derived from any real Canadian construction
 * cost index. See ./README.md.
 *
 * Deliberately sparse at the city/postal tier: only two provinces get an
 * illustrative city-level entry (Ontario/Toronto, British Columbia/
 * Vancouver) and one gets a postal-level entry, specifically to
 * demonstrate that resolution falls back gracefully when finer-grained
 * data doesn't exist - which, for most of the country right now, it
 * doesn't. See docs/regional-pricing.md.
 */

const SAMPLE_EFFECTIVE_DATE = "2026-08-24";
const SAMPLE_DISCLOSURE =
  "Administrator supplied sample value. Not derived from any verified Canadian regional construction cost index - for architecture/testing purposes only.";

let counter = 0;
function nextId(prefix: string): string {
  counter += 1;
  return `${prefix}_${counter.toString().padStart(3, "0")}`;
}

function provinceEntry(province: RegionalMultiplierEntry["province"], multiplier: number, note?: string): RegionalMultiplierEntry {
  return {
    id: nextId("region_province"),
    tier: "province",
    province,
    city: null,
    postalFsaPrefix: null,
    multiplier,
    effectiveDate: SAMPLE_EFFECTIVE_DATE,
    sourceId: null,
    sourceDate: null,
    confidenceLevel: "administrator_sample_value",
    notes: note ? `${SAMPLE_DISCLOSURE} ${note}` : SAMPLE_DISCLOSURE
  };
}

function cityEntry(
  province: RegionalMultiplierEntry["province"],
  city: string,
  multiplier: number,
  note?: string
): RegionalMultiplierEntry {
  return {
    id: nextId("region_city"),
    tier: "city",
    province,
    city,
    postalFsaPrefix: null,
    multiplier,
    effectiveDate: SAMPLE_EFFECTIVE_DATE,
    sourceId: null,
    sourceDate: null,
    confidenceLevel: "administrator_sample_value",
    notes: note ? `${SAMPLE_DISCLOSURE} ${note}` : SAMPLE_DISCLOSURE
  };
}

function postalEntry(
  province: RegionalMultiplierEntry["province"],
  postalFsaPrefix: string,
  multiplier: number,
  note?: string
): RegionalMultiplierEntry {
  return {
    id: nextId("region_postal"),
    tier: "postal",
    province,
    city: null,
    postalFsaPrefix,
    multiplier,
    effectiveDate: SAMPLE_EFFECTIVE_DATE,
    sourceId: null,
    sourceDate: null,
    confidenceLevel: "administrator_sample_value",
    notes: note ? `${SAMPLE_DISCLOSURE} ${note}` : SAMPLE_DISCLOSURE
  };
}

// One entry per supported province/territory, all relative to the 1.0
// national baseline. Sample values only - directionally plausible (higher
// in BC/ON metro-heavy provinces and the territories, lower in some
// Atlantic/Prairie provinces) but not sourced.
export const sampleProvinceMultipliers: RegionalMultiplierEntry[] = [
  provinceEntry("ON", 1.05),
  provinceEntry("QC", 0.95),
  provinceEntry("BC", 1.1),
  provinceEntry("AB", 1.0),
  provinceEntry("SK", 0.9),
  provinceEntry("MB", 0.9),
  provinceEntry("NS", 0.92),
  provinceEntry("NB", 0.88),
  provinceEntry("NL", 0.93),
  provinceEntry("PE", 0.9),
  provinceEntry("YT", 1.35, "Territories carry significant freight/logistics cost premiums even as a sample placeholder."),
  provinceEntry("NT", 1.4, "Territories carry significant freight/logistics cost premiums even as a sample placeholder."),
  provinceEntry("NU", 1.5, "Nunavut has no year-round road access to most communities; sample multiplier reflects that directionally only.")
];

// Sparse - illustrates that only some provinces have city-level data.
export const sampleCityMultipliers: RegionalMultiplierEntry[] = [
  cityEntry("ON", "Toronto", 1.18, "Major metro premium over the ON province-level baseline."),
  cityEntry("BC", "Vancouver", 1.25, "Major metro premium over the BC province-level baseline.")
];

// Sparse - illustrates postal-FSA-level precision where it exists at all.
export const samplePostalMultipliers: RegionalMultiplierEntry[] = [
  postalEntry("ON", "M5V", 1.22, "Downtown Toronto core FSA; slightly above the city-level Toronto sample multiplier.")
];

export const allSampleRegionalMultipliers: RegionalMultiplierEntry[] = [
  ...sampleProvinceMultipliers,
  ...sampleCityMultipliers,
  ...samplePostalMultipliers
];
